import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useInventoryApi from "./useInventoryApi";
import "./Inventory.css";

const STATUS_PILL = { received: "success", closed: "success", sent: "warn", draft: "muted" };

function lineTotal(po) {
  return (po.lines || []).reduce((s, l) => s + Number(l.qty || 0) * Number(l.price || 0), 0);
}
function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
}
function fmtMoney(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function OrdersPage() {
  const { listOrders } = useInventoryApi();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    let live = true;
    setLoading(true);
    listOrders()
      .then((r) => { if (live) setOrders(Array.isArray(r) ? r : r?.results || []); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [listOrders]);

  const stats = useMemo(() => {
    const open = orders.filter((o) => o.status === "draft" || o.status === "sent").length;
    const inTransit = orders.filter((o) => o.status === "sent").length;
    const received = orders.filter((o) => o.status === "received").length;
    const value = orders.reduce((s, o) => s + lineTotal(o), 0);
    return { open, inTransit, received, value };
  }, [orders]);

  const filtered = useMemo(() => {
    return orders.filter((o) => {
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (q) {
        const hay = `${o.id} ${o.vendor_name || ""}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [orders, q, statusFilter]);

  return (
    <div className="inventory-page">
      <header className="inventory-head">
        <div>
          <h1>Orders</h1>
          <p className="muted">Purchase orders and receiving.</p>
        </div>
        <div className="filters">
          <input placeholder="Search PO or vendor…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="received">Received</option>
            <option value="closed">Closed</option>
          </select>
          <button className="btn mini" onClick={() => navigate("../products")}>+ New PO</button>
        </div>
      </header>

      <section className="inventory-grid">
        <div className="inventory-stat"><div className="label">Open POs</div><div className="value">{stats.open}</div></div>
        <div className="inventory-stat"><div className="label">In Transit</div><div className="value">{stats.inTransit}</div></div>
        <div className="inventory-stat"><div className="label">Received</div><div className="value">{stats.received}</div></div>
        <div className="inventory-stat"><div className="label">Total Value</div><div className="value">{fmtMoney(stats.value)}</div></div>
      </section>

      <section className="inventory-card">
        <div className="inventory-table" style={{ "--inventory-columns": "1.1fr 1.6fr 0.9fr 1fr 0.9fr 0.6fr" }}>
          <div className="inventory-thead">
            <div>PO</div>
            <div>Vendor</div>
            <div>Status</div>
            <div>Expected</div>
            <div>Total</div>
            <div>Items</div>
          </div>
          {loading ? (
            <div className="inventory-row muted">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="inventory-empty">
              <div className="value" style={{ fontSize: 15 }}>No purchase orders yet</div>
              <p className="muted" style={{ margin: "6px 0 0" }}>
                POs created from low-stock reorders or the receipt rail appear here.
              </p>
            </div>
          ) : (
            filtered.map((o) => (
              <div key={o.id} className="inventory-row">
                <div className="mono">PO-{o.id}</div>
                <div>{o.vendor_name || "—"}</div>
                <div><span className={`inventory-pill ${STATUS_PILL[o.status] || "muted"}`}>{o.status}</span></div>
                <div>{fmtDate(o.expected_at)}</div>
                <div>{fmtMoney(lineTotal(o))}</div>
                <div>{(o.lines || []).length}</div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
